/**
 * Frontend error handling utilities
 * Provides consistent error handling and user-friendly error messages
 */

export interface ApiError {
  code: string;
  message: string;
  details?: any;
  stack?: string;
}

export interface ApiErrorResponse {
  success: false;
  error: ApiError;
}

/**
 * Extract error message from various error formats
 */
export const getErrorMessage = (error: any): string => {
  // Handle API error responses
  if (error?.response?.data?.error?.message) {
    return error.response.data.error.message;
  }

  // Handle API error with just message
  if (error?.response?.data?.message) {
    return error.response.data.message;
  }

  // Handle network errors
  if (error?.message === 'Network Error') {
    return 'Unable to connect to the server. Please check your internet connection.';
  }

  // Handle timeout errors
  if (error?.code === 'ECONNABORTED') {
    return 'Request timed out. Please try again.';
  }

  // Handle standard Error objects
  if (error?.message) {
    return error.message;
  }

  // Handle string errors
  if (typeof error === 'string') {
    return error;
  }

  // Default error message
  return 'An unexpected error occurred. Please try again.';
};

/**
 * Get user-friendly error message based on error code
 */
export const getUserFriendlyMessage = (errorCode: string): string => {
  const messages: Record<string, string> = {
    // Authentication errors
    AUTHENTICATION_ERROR: 'Invalid username or password. Please try again.',
    AUTHORIZATION_ERROR: 'You do not have permission to perform this action.',
    TOKEN_EXPIRED: 'Your session has expired. Please log in again.',
    
    // Validation errors
    VALIDATION_ERROR: 'Please check your input and try again.',
    INVALID_INPUT: 'The information provided is invalid.',
    
    // Resource errors
    NOT_FOUND: 'The requested resource was not found.',
    ROUTE_NOT_FOUND: 'The page you are looking for does not exist.',
    
    // Conflict errors
    CONFLICT: 'This resource already exists.',
    DUPLICATE_ENTRY: 'A record with this information already exists.',
    
    // External API errors
    EXTERNAL_API_ERROR: 'Unable to connect to external service. Please try again later.',
    
    // Payment errors
    INSUFFICIENT_CREDIT: 'Insufficient account credit. Please top up your account.',
    PAYMENT_FAILED: 'Payment processing failed. Please try again.',
    
    // Rate limiting
    RATE_LIMIT_EXCEEDED: 'Too many requests. Please wait a moment and try again.',
    
    // Server errors
    INTERNAL_SERVER_ERROR: 'An unexpected error occurred. Please try again later.',
    DATABASE_ERROR: 'A database error occurred. Please try again later.',
    
    // Network errors
    NETWORK_ERROR: 'Unable to connect to the server. Please check your connection.',
    TIMEOUT_ERROR: 'Request timed out. Please try again.',
  };

  return messages[errorCode] || 'An unexpected error occurred. Please try again.';
};

/**
 * Log error to console (and optionally to external service)
 */
export const logError = (error: any, context?: string) => {
  const errorLog = {
    message: getErrorMessage(error),
    context,
    timestamp: new Date().toISOString(),
    userAgent: navigator.userAgent,
    url: window.location.href,
    error: error,
  };

  // Log to console in development
  if (import.meta.env.MODE !== 'production') {
    console.error('Error logged:', errorLog);
  }

  // In production, send to external error tracking service
  // Example: Sentry, LogRocket, etc.
  // if (process.env.NODE_ENV === 'production') {
  //   sendToErrorTrackingService(errorLog);
  // }
};

/**
 * Handle API errors consistently
 */
export const handleApiError = (error: any, context?: string): string => {
  logError(error, context);

  // Extract error code if available
  const errorCode = error?.response?.data?.error?.code;
  
  if (errorCode) {
    return getUserFriendlyMessage(errorCode);
  }

  return getErrorMessage(error);
};

/**
 * Show error notification (to be used with a toast/notification library)
 */
export const showErrorNotification = (error: any, context?: string) => {
  const message = handleApiError(error, context);
  
  // For now, just use alert (replace with proper notification library)
  // In production, use something like react-toastify, notistack, etc.
  console.error(message);
  
  return message;
};

/**
 * Retry function with exponential backoff
 */
export const retryWithBackoff = async <T>(
  fn: () => Promise<T>,
  maxRetries: number = 3,
  baseDelay: number = 1000
): Promise<T> => {
  let lastError: any;

  for (let i = 0; i < maxRetries; i++) {
    try {
      return await fn();
    } catch (error: any) {
      lastError = error;
      
      // Don't retry on customer errors (4xx)
      if (error?.response?.status >= 400 && error?.response?.status < 500) {
        throw error;
      }

      // Wait before retrying (exponential backoff)
      if (i < maxRetries - 1) {
        const delay = baseDelay * Math.pow(2, i);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }

  throw lastError;
};

/**
 * Check if error is a network error
 */
export const isNetworkError = (error: any): boolean => {
  return (
    error?.message === 'Network Error' ||
    error?.code === 'ECONNABORTED' ||
    !error?.response
  );
};

/**
 * Check if error is an authentication error
 */
export const isAuthError = (error: any): boolean => {
  const status = error?.response?.status;
  const code = error?.response?.data?.error?.code;
  
  return (
    status === 401 ||
    code === 'AUTHENTICATION_ERROR' ||
    code === 'TOKEN_EXPIRED'
  );
};

/**
 * Check if error is an authorization error
 */
export const isAuthorizationError = (error: any): boolean => {
  const status = error?.response?.status;
  const code = error?.response?.data?.error?.code;
  
  return status === 403 || code === 'AUTHORIZATION_ERROR';
};
